// The hub's panel contract and its markup primitives (R229).
//
// A panel is a small object with one job: turn what `HubData` holds into a
// string of markup, and act on the presses that come back out of it. The
// shell owns the chrome, the scrolling, the fade, the tabs, the badge dots
// and the audio cues; a panel owns its own content and nothing else.
//
// Three rules every panel keeps:
//
//   1. **Escape everything a person wrote.** A clan name, a chat line, a
//      news title and a server's error message all reach `esc` before they
//      reach the document. News bodies are Markdown and travel as data
//      (R154), so a panel prints one as text and never as HTML.
//   2. **Let the shell route the press.** The pane scrolls under the
//      finger, so a panel control acts on the release (R1206). `act` reads
//      the data attributes of the element it gets and never the document,
//      because the shell rebuilds the pane on every state change.
//   3. **An empty answer is a true answer.** Where a route serves an empty
//      table, the panel says what is not there. It never draws a made-up
//      row to fill a card.

import { oneOf } from '../../util/oneof';
import type { SfxId } from '../../audio/triggers';
import { messageOf } from '../../net/api';
import type { JoinRequest, Me } from '../../net/api';
import { hubPanel as S } from '../../gen/strings';
import { factionColor, factionName, hex } from '../../world';
import type { ArmGate } from '../arm';
import { button, linkButton, type Press } from '../choice/choice';
import { SECTION_IDS, type PageId, type SectionId } from '../options/sections';
import { tierOf, type Cell, type HubData, type Tier } from './hubstate';
import { UNCHARTED_MARK } from './uncharted';

/**
 * The ten sections of the hub. The first five are the bottom dock and the
 * last five are the utility strip under the header.
 *
 * The list is a value as well as a type, because a page address may name a
 * section (`?tab=settings`, which is where a link on a sign-in provider
 * comes back to — R621). A caller checks a name against this list before it
 * hands it to the hub.
 */
export const TAB_KEYS = [
  'play', 'ranks', 'record', 'clan', 'wardroom',
  'news', 'shop', 'mods', 'support', 'settings',
] as const;

export type TabKey = (typeof TAB_KEYS)[number];

/** The address parameter that names the open section. `fast/entry.ts`
 *  reads it, and the hub writes it on every change of section (R1738). */
export const TAB_PARAM = 'tab';

/**
 * The address parameter that names a page inside a section, and the pages
 * it names. `?tab=clan&page=cup` opens the CUP page of the CLAN section,
 * the deep link of a clan post about the clan cup (R1649). The meta server
 * writes the same two words into that link: `hubPageParam` and `hubPageCup`
 * of `server/internal/metasrv/httpapi/discord.go`. `?tab=record&page=chronicle`
 * opens the season chronicle book, the way back from a replay link (R1684).
 */
export const PAGE_PARAM = 'page';
export const PAGE_CUP = 'cup';
export const PAGE_CHRONICLE = 'chronicle';

/**
 * The address parameter that names a section of a panel in the section
 * shell (R1731, ui-map-interface.md §16.16): `?tab=settings&section=alerts`
 * opens the ALERTS page of SETTINGS, and `?tab=record&section=chronicle`
 * the CHRONICLE section of RECORD. The in-game ACCOUNT page links the hub
 * rows with it. The hub reads it off the address itself (`hub.ts`) and
 * hands the word to the `openSection` of the open panel, which knows its
 * own section ids and aliases (`sectionshell.ts` `SectionNav`). A sign-in
 * with a provider carries it through the state cookie beside `tab` and
 * `page` (`oauthroutes.go`, R1659).
 */
export const SECTION_PARAM = 'section';

/** The form of a section word of the address: a short lower-case id. */
const SECTION_WORD = /^[a-z][a-z0-9-]{0,31}$/;

/**
 * The pages of the hub SETTINGS panel, in the one order of R1731: the
 * sections of the ☸ OPTIONS menu less HELP, which holds tools of a world
 * alone, and the ACCOUNT page.
 */
export const SETTINGS_PAGES: readonly Exclude<PageId, 'help'>[] = [
  ...SECTION_IDS.filter((id): id is Exclude<SectionId, 'help'> => id !== 'help'), 'account',
];

/** One page of the hub SETTINGS panel. */
export type SettingsPage = (typeof SETTINGS_PAGES)[number];

/**
 * The section word the address names, or undefined for none or for a word
 * of the wrong form. The word is not checked against a panel here: the
 * panel the address opens knows its own ids and drops a word it does not
 * know (§16.16).
 */
export function readSection(params: URLSearchParams): string | undefined {
  const want = params.get(SECTION_PARAM) ?? '';
  return SECTION_WORD.test(want) ? want : undefined;
}

/**
 * The six boards of RANKS (R1737). A press in another section opens RANKS
 * on one of them as a page: `ctx.go('ranks', 'ladder')` (R1738). The
 * address names no board, so `TAB_PAGES` does not list them.
 */
export const RANKS_BOARDS = ['honour', 'clans', 'seasons', 'memorials', 'achievements', 'ladder'] as const;

/** One board of RANKS. */
export type RanksBoard = (typeof RANKS_BOARDS)[number];

/** The RANKS board `key` names, or undefined for a word it does not know. */
export function readBoard(key: string): RanksBoard | undefined {
  return oneOf(RANKS_BOARDS, key);
}

/** A page a section opens on: a page of `TAB_PAGES` or a SETTINGS page of
 *  `SECTION_PARAM` from the address, or a RANKS board from a press in
 *  another section (R1738). */
export type PageKey = typeof PAGE_CUP | typeof PAGE_CHRONICLE | SettingsPage | RanksBoard;

/** A section of a panel in the section shell, for a press in another
 *  panel that means that section: `ctx.go('mods', { section: 'themes' })`.
 *  The hub hands the word to the `openSection` of the panel, as it does
 *  for the address word `SECTION_PARAM` (§16.16). */
export interface SectionAt {
  section: string;
}

/** The pages each section opens on from the address. A caller drops a page
 *  the named section does not list, as it drops a section `TAB_KEYS` does
 *  not name. */
export const TAB_PAGES: Readonly<Partial<Record<TabKey, readonly PageKey[]>>> = {
  clan: [PAGE_CUP],
  record: [PAGE_CHRONICLE],
};

/** What the shell hands every panel. Nothing here draws; a panel calls
 *  `refresh` and the shell decides when to redraw. */
export interface PanelContext {
  /** The account, as the shell last read it. */
  me(): Me;
  /** Replaces the account after a rename, a purchase or a re-read. It
   *  also drops the cached account, so the next render cannot take the
   *  old one back. */
  setMe(me: Me): void;
  /** The cache. Every read a panel makes goes through it. */
  data: HubData;
  /**
   * The server's own instant, in unix seconds, carried forward by the
   * newest answer that held one. Every countdown is measured against this
   * and not against the browser's clock, because a telephone that has been
   * asleep carries a clock that has drifted (R220).
   */
  now(): number;
  /** Asks the shell to draw the pane again. The shell diffs the markup, so
   *  a call that changes nothing costs one string compare. */
  refresh(): void;
  /** Leaves the hub for a world. The shell closes its chat socket first. */
  join(req: JoinRequest): void;
  /** Opens another section, for a card that points at the shop. `at`
   *  opens a page of that section through its `openPage`, before its
   *  `open`: `go('ranks', 'ladder')` opens RANKS on ARCADE LADDER (R1738).
   *  A `SectionAt` opens a section through its `openSection` instead: a
   *  press that means one section names it (§16.16). */
  go(tab: TabKey, at?: PageKey | SectionAt): void;
  /** True while the pane is wide enough for the two-column layout. */
  wide(): boolean;
  /** Plays one menu cue. A client with no director is silent. */
  play(id: SfxId): void;
  /**
   * While `on`, the hub leaves the menu playlist and plays the player's own
   * playlist; off takes the menu playlist again (R815). The MODDING Music
   * row samples a pick with it. A client with no director is silent.
   */
  musicSample?(on: boolean): void;
}

/** One section of the hub. */
export interface Panel {
  readonly key: TabKey;
  /** The section became visible. A panel starts its reads here. */
  open?(): void;
  /** The hub opens on a page of this section, because the address named it
   *  (`TAB_PAGES`, R1649; `SECTION_PARAM` for SETTINGS, R1731), or because
   *  a press in another section named one through `ctx.go` (R1738). It runs
   *  before the `open` of that visit. `book` is the world whose live volume
   *  the chronicle book opens on, or 0 (R1684). */
  openPage?(page: PageKey, book?: number): void;
  /** The hub opens on a section of this panel, because the address named
   *  it (`SECTION_PARAM`, §16.16). The word is any word of the right form;
   *  a panel in the section shell opens the section it names, or the one
   *  its alias names, and drops any other word. It runs before `open`. */
  openSection?(word: string): void;
  /** The section was hidden. A panel gives its canvases back here. */
  close?(): void;
  /**
   * The hub itself is going. A panel that holds something outside its own
   * markup — the CLAN panel's chat socket — lets it go here. The shell
   * calls this before it resolves a join, because `main.ts` opens the
   * in-game chat socket after the boot and two sockets on one clan channel
   * is one too many (R225).
   */
  destroy?(): void;
  /** The whole content of the pane, as markup. */
  html(): string;
  /** One press on an element that carries `data-act`. */
  act?(action: string, el: HTMLElement): void;
  /** Runs after every render: inputs, focus and anything not a button. */
  wire?(root: HTMLElement): void;
  /** The badge dot, where this section owns one. */
  dot?(): boolean;
}

// --- markup primitives -----------------------------------------------------

/** Escapes what a person wrote. Every panel puts every such string through
 *  it, including a server error message. It is the chrome's one escape. */
import { esc } from '../esc';
export { esc };

/**
 * One section button, for the utility strip and for the bottom dock
 * (ui-map-interface.md §16.3, R231).
 *
 * The button carries the name and nothing else. A glyph beside a name is a
 * caption, and these ten are controls: the name is the button, at the size
 * a control's label takes. The dot rides beside the name, because a name
 * that grows moves an absolutely placed dot with it. The open section wears
 * `aria-current="page"`, and no other tab does (R1738).
 */
export function tabButton(key: TabKey, name: string, on: boolean, dot: boolean): string {
  return `<button type="button" class="t${on ? ' on' : ''}"${on ? ' aria-current="page"' : ''}`
    + ` data-act="tab" data-tab="${esc(key)}">`
    + `<span class="n">${esc(name)}</span>`
    // The dot says its word to a screen reader: "CLAN, NEW" (R2153).
    + `${dot ? `<span class="dot"></span>${saidOnly(S.dot_said)}` : ''}</button>`;
}

/** The inline style of a word that a screen reader reads and the page never
 *  paints: the rule of the hub's live region (`#hub .live`), on the element
 *  itself, so every sheet that draws a hub builder hides it (R2153). */
const SAID_ONLY_STYLE = 'position:absolute;width:1px;height:1px;overflow:hidden;'
  + 'clip-path:inset(50%);white-space:nowrap';

/** A word for a screen reader alone (R2153): the joint between a heading
 *  and its subtitle, the word of a badge dot. The word is text of its
 *  element, so it joins the accessible name of the heading or the button. */
export function saidOnly(word: string): string {
  return `<span style="${SAID_ONLY_STYLE}">${esc(word)}</span>`;
}

/**
 * The whole content of a section, as one column (R231).
 *
 * Every panel returns its top-level blocks through this. It is what makes
 * the gap between two full-width blocks the same gap that two stacked
 * half-width cards already show: `.page`, `.col` and `.split` all take
 * `--hub-gap`, and the sheet clears the stray margin an `h1` or a button row
 * carries, so the first gap is the same size as the rest. A panel must not
 * add a `margin-top` of its own to space a block.
 *
 * An empty part is dropped, so a panel may pass a line that is not always
 * there without leaving an empty gap behind it.
 */
export function page(...parts: string[]): string {
  return `<div class="page">${parts.filter((p) => p !== '').join('')}</div>`;
}

/** The section heading: a name and a quiet subtitle. It is the one `h1`
 *  of the page, because each hub section is a page (`aria-current="page"`
 *  on its tab), and `hub.css` draws the heading as the `div` it was (R2048).
 *  A joint that only a screen reader reads stands between the name and the
 *  subtitle, so the heading reads "RANKS, ALL PLAYERS" and not "RANKSALL
 *  PLAYERS" (R2153). */
export function h1(name: string, sub = ''): string {
  return `<h1 class="h1">${esc(name)}`
    + `${sub === '' ? '' : `${saidOnly(S.heading_join)}<span class="sub">${esc(sub)}</span>`}</h1>`;
}

/** One bordered card. `more` is the quiet figure on its right. */
export function card(title: string, glyph: string, more: string, inner: string, cls = ''): string {
  return frame(title, glyph, `<span class="more">${esc(more)}</span>`, inner, cls);
}

/** One bordered card that wears a tier tag (`tierTag`, `lockedTag`) on
 *  the right of its heading, in place of the quiet figure (R1547). */
export function tagCard(title: string, glyph: string, tag: string, inner: string, cls = ''): string {
  return frame(title, glyph, tag, inner, cls);
}

/**
 * One section of a page with more than one section (§16.16 "The page
 * body"): the sub-header of SETTINGS · SOCIAL · BLOCKED ACCOUNTS (the
 * name, the quiet `aside` such as "0 of 200", the hairline), over the
 * content, with no box. `mark` stands after the name (a tier tag). The
 * block keeps the `card` class, so the rules of the content still hold.
 */
export function section(title: string, aside: string, inner: string, cls = '', mark = ''): string {
  return `<div class="card sect${cls === '' ? '' : ` ${cls}`}">`
    + `<h3 class="o-sec"><b>${esc(title)}</b>${mark}${aside === '' ? '' : `<i>${esc(aside)}</i>`}</h3>${inner}</div>`;
}

/** The body of a page with one section: the content alone, with no box
 *  and no heading, because the page title names it (§16.16). */
export function plain(inner: string, cls = ''): string {
  return `<div class="card sect${cls === '' ? '' : ` ${cls}`}">${inner}</div>`;
}

/** The figure an old card heading carried in its corner, as the first
 *  line of a page with one section (R1945): the page title names the
 *  section, so the figure keeps its own quiet line. '' draws nothing. */
export function figLine(text: string): string {
  return text === '' ? '' : `<div class="pfig">${esc(text)}</div>`;
}

function frame(title: string, glyph: string, right: string, inner: string, cls: string): string {
  return `<div class="card${cls === '' ? '' : ` ${cls}`}">`
    + `<h2 class="h"><span class="g" aria-hidden="true">${esc(glyph)}</span>${esc(title)}`
    + `<span class="sp"></span>${right}</h2>${inner}</div>`;
}

/** One key/value row of a card. The third field is a colour class. */
export type Row = [key: string, value: string, cls?: string];

export function rows(list: Row[]): string {
  return list.map(([k, v, c]) =>
    `<div class="row"><span class="k">${esc(k)}</span>`
    + `<span class="v${c ? ` ${c}` : ''}">${esc(v)}</span></div>`).join('');
}

/** A paragraph of prose inside a card. */
export function lede(text: string): string {
  return `<div class="lede">${esc(text)}</div>`;
}

/**
 * The frame every settings row shares: the name and the quiet line on the
 * left, and the control on the right. `desc` is markup, so a caller escapes
 * what a person wrote. The DISCORD, CHAT RELAY and MODDING cards draw their
 * rows through it. The SETTINGS panel draws the rows of the ☸ OPTIONS kit
 * instead (`ui/options/kit.ts`, R1731).
 */
export function srow(name: string, desc: string, control: string, cls = ''): string {
  return `<div class="srow${cls === '' ? '' : ` ${cls}`}"><span class="sk">`
    + `<span class="sn">${esc(name)}</span>`
    + `<span class="sd">${desc}</span></span>${control}</div>`;
}

/**
 * One switch control, ON or OFF, in the one pair of words `[ui.hub_panel]`
 * carries for the hub cards that draw a switch. `attrs` names the action,
 * its data and whether the switch is held. `label` names the setting: it is
 * the accessible name, so a screen reader reads the setting and its pressed
 * state and not the bare word OFF (R1738). The WARDROOM and the fleet card
 * use the same `aria-pressed` idiom.
 */
export function switchButton(on: boolean, attrs: string, label: string): string {
  return `<button type="button" class="sw${on ? '' : ' off'}" aria-pressed="${on}"`
    + ` aria-label="${esc(label)}"${attrs}>${on ? S.switch_on : S.switch_off}</button>`;
}

/**
 * One text box of a form. It carries its text back as a value, because the
 * pane is rewritten whenever the markup changes, and the shell finds the
 * box a player types in again by its `data-in` name. `attrs` is the type
 * and the input hints. An empty `placeholder` writes none, for a box that
 * carries a visible label (`field`).
 */
export function textBox(name: string, attrs: string, placeholder: string, value: string): string {
  const hint = placeholder === '' ? '' : ` placeholder="${esc(placeholder)}"`;
  return `<input data-in="${name}" ${attrs}${hint} value="${esc(value)}">`;
}

/**
 * One text box with a visible label over it (R1738). A placeholder is no
 * label: it goes the moment a player types, and a screen reader may not
 * read it. `box` is the markup of a box whose `id` is `id`, and the label
 * names it with `for`. `label` is text and is escaped here. `cls` adds
 * a class to the field, for a form that sizes its boxes apart (R2155).
 */
export function field(id: string, label: string, box: string, cls = ''): string {
  const c = cls === '' ? 'field' : `field ${cls}`;
  return `<span class="${c}"><label for="${esc(id)}">${esc(label)}</label>${box}</span>`;
}

/**
 * One row of controls under a form or a card (R2011): buttons, tags, a
 * tab strip or a pill group of the choice kit. It wraps between its
 * controls and keeps room under it. Each kit press shows a hover, a
 * press and a focus ring.
 */
export function ctlRow(inner: string): string {
  return `<div class="hctl">${inner}</div>`;
}

/**
 * The way back of a sub-page (R1751): one text bar at the top left of the
 * page, "‹ PARENT", in the form of the ‹ SETTINGS head of a narrow
 * SETTINGS page. The CUP page under CLAN and the chronicle book under
 * RECORD take it. The press goes through the shell's press router, as
 * every `data-act` control does.
 */
export function backBar(parent: string, act: string, here = ''): string {
  return `<div class="hback"><button type="button" class="hback-b" data-act="${esc(act)}">`
    + `‹ ${esc(parent)}</button>`
    + (here === '' ? '' : `<h2 class="hback-here">${esc(here)}</h2>`) + '</div>';
}

/**
 * One destructive control (R837): a danger button that shows `armFace`,
 * the word that names the act, while it is armed, and wears the armed
 * state; both faces stand in one cell, so the press keeps its width
 * (R2069). `armedPress` is its press. A null `press` draws it held, for a
 * card whose call is in flight.
 */
export function armedButton(gate: ArmGate, key: string, label: string, armFace: string, press: Press | null): string {
  return button(label, press ?? { act: key },
    { variant: 'danger', armFace, armed: gate.is(key), disabled: press === null });
}

/** The press on an armed control. The first press arms it and plays the
 *  press cue, because the gate plays no sound of its own; the second
 *  press, inside the window, answers true. */
export function armedPress(ctx: Pick<PanelContext, 'play'>, gate: ArmGate, key: string): boolean {
  if (gate.press(key)) return true;
  ctx.play('sfx-menu-press');
  return false;
}

/**
 * One faction as a label, in the colour `world.ts` gives it. The colour
 * is inline because the map's legend holds five of them and the chrome's
 * stylesheet holds one accent: a class for each faction would be a second
 * copy of a table this client already has. The caller checks that `id`
 * names a faction the game has.
 */
export function factionTag(id: number): string {
  const ink = hex(factionColor(id));
  return `<em class="ch-tag fac" style="color:${ink};border-color:${ink}">`
    + `${esc(factionName(id).toUpperCase())}</em>`;
}

/**
 * The one word of each plan (R2046). The header badge, the SHOP, the PLAY
 * corner and every tier tag read it, so one account reads one plan word
 * in every place of the hub.
 */
export const PLAN_WORDS: Readonly<Record<Tier, string>> = {
  free: S.tier_free, supporter: S.tier_supporter, premium: S.tier_premium,
};

/** The price order of the plans: a plan includes every plan under it
 *  (monetization.md §4, §5). */
const PLAN_RANK: Readonly<Record<Tier, number>> = { free: 0, supporter: 1, premium: 2 };

/** The ink class of a tier tag: the Premium ink, else the Supporter ink. */
function planInk(plan: Tier): string {
  return plan === 'premium' ? 'prem' : 'sup';
}

/** True when `t` names a plan of the hub, and not a bare key. */
function isPlan(t: string): t is Tier {
  return t === 'free' || t === 'supporter' || t === 'premium';
}

/** The word of a gate: the plan word, or the bare key in capitals for a
 *  key that no plan sells. */
function gateWord(gate: string): string {
  return isPlan(gate) ? PLAN_WORDS[gate] : gate.toUpperCase();
}

/** True when the account holds the key through its own plan: the plan
 *  is the gate's plan or a higher one. */
function included(gate: string, held: boolean, plan: Tier): boolean {
  return held && isPlan(gate) && PLAN_RANK[plan] >= PLAN_RANK[gate];
}

/**
 * What a gated feature says to this account, as one line (R2046). `gate`
 * is the plan that sells the key, `held` whether the account holds the
 * key, and `me` the account, whose own plan `tierOf` reads.
 *
 * - The plan includes the key: INCLUDED IN PREMIUM, with the account's
 *   own plan word, never the lower gate word.
 * - The account holds the key without the plan (a grant, or the closed
 *   test's `everyone` list, monetization.md §5): SUPPORTER · GRANTED.
 * - The account does not hold the key: SUPPORTER · NOT IN YOUR PLAN.
 */
export function tierLine(gate: string, held: boolean, me: Me): string {
  const plan = tierOf(me);
  if (included(gate, held, plan)) return S.included_in({ plan: PLAN_WORDS[plan] });
  return S.tier_line({ tier: gateWord(gate), state: held ? S.tier_granted : S.tier_not_in_plan });
}

/** The tier tag a gated card wears (R2046). The tag stays in full colour
 *  whether the key is held or not; only the content behind it fades. A
 *  plan that includes the key fills the tag with INCLUDED IN and the
 *  plan word, in the ink of that plan. */
export function tierTag(tier: 'supporter' | 'premium', held: boolean, me: Me): string {
  const plan = tierOf(me);
  if (included(tier, held, plan)) {
    return `<div class="tier"><span class="tag ${planInk(plan)}">`
      + `${S.included_in({ plan: PLAN_WORDS[plan] })}</span></div>`;
  }
  return `<div class="tier"><span class="tag ${planInk(tier)}">${PLAN_WORDS[tier]}</span>`
    + `<span class="${held ? 'held' : 'unheld'}">${held ? S.tier_granted : S.tier_not_in_plan}</span></div>`;
}

/**
 * The tag a row wears while the account does not hold what the row shows
 * (effects.md §3, R908).
 *
 * It is the markup of `tierTag` with no tier name: an effect is granted by
 * an operator or awarded by the season report, so no tier buys one, and the
 * caller passes the word the tag carries. The account never holds such a
 * row, so the tag always says so.
 */
export function lockedTag(label: string): string {
  return `<div class="tier"><span class="tag lock">${esc(label)}</span>`
    + `<span class="unheld">${S.not_held}</span></div>`;
}

/**
 * The one forward press of an empty state (R1742). It leads to a real
 * place: `tab` opens a hub section through the shell's own tab switch,
 * `href` opens a page in this tab, and `act` is a press of the panel that
 * draws the state.
 */
export type EmptyPress =
  | { label: string; tab: TabKey; section?: string }
  | { label: string; href: string }
  | { label: string; act: string; data?: Record<string, string> };

/** The data of a tab press: the tab, and the section it means when it
 *  means one (R1910, "A link names the section it means"). */
export function tabData(tab: TabKey, section?: string): Record<string, string> {
  return section === undefined ? { tab } : { tab, section };
}

/** The markup of one forward press: a primary button (R2011). */
function emptyPress(p: EmptyPress): string {
  if ('tab' in p) return button(p.label, { act: 'tab', data: tabData(p.tab, p.section) }, { variant: 'primary' });
  if ('href' in p) return linkButton(p.label, p.href, { variant: 'primary' });
  return button(p.label, { act: p.act, data: p.data }, { variant: 'primary' });
}

/**
 * The honest empty state. It says what is not there and why, and it never
 * stands in for a row. Every stub route in the hub renders one of these.
 *
 * It reads as uncharted water (R1742): the mark of `UNCHARTED_MARK` on a
 * hatched ground. `press` is the one way forward, where a real one exists;
 * a state with nowhere to go takes none.
 */
export function empty(what: string, why = '', press?: EmptyPress): string {
  return `<div class="empty uw">${UNCHARTED_MARK}<span class="e1">${esc(what)}</span>`
    + (why === '' ? '' : `<span class="e2${/\p{Ll}/u.test(why) ? ' mc' : ''}">${esc(why)}</span>`)
    + (press === undefined ? '' : ctlRow(emptyPress(press))) + '</div>';
}

/** The line a card shows while its read is in flight. A dash, never a
 *  guess: an invented figure is worse than a wait. */
export function loading(): string {
  return '<div class="empty"><span class="e1">…</span></div>';
}

/** The refusal line under a control, or '' while nothing was refused. A
 *  server's own message reaches it as text and never as markup. */
export function errLine(text: string): string {
  return text === '' ? '' : `<div class="err">${esc(text)}</div>`;
}

/** The line a card shows when its read failed. The server's own message is
 *  quoted, because a person may read it and a client may not parse it. The
 *  sentence comes from `messageOf`, which is the one rule for every screen
 *  and which names no class (R1048). */
export function failed(err: unknown): string {
  const msg = messageOf(err);
  return `<div class="empty bad"><span class="e1">${S.did_not_load}</span>`
    + `<span class="e2">${esc(msg)}</span></div>`;
}

/**
 * What a card shows in place of a read that holds no value yet: the failed
 * line for a read that failed, and the loading line otherwise. A cell holds
 * no value exactly while it is not `ready`, so a caller tests
 * `cell.value === undefined` and draws this.
 */
export function pending(cell: Cell<unknown>): string {
  return cell.state === 'failed' ? failed(cell.error) : loading();
}

/** One link button to a page in a tab of its own, in a row of its own.
 *  It is never an `act`, because the press opens a page and changes
 *  nothing here. */
export function linkRow(href: string, label: string): string {
  return ctlRow(linkButton(label, href, { variant: 'primary', blank: true }));
}

/** An id or a body key, as a person reads it: `caustic-sweep` becomes
 *  CAUSTIC SWEEP, and `ownFleet` becomes OWN FLEET (R822). */
export function readable(id: string): string {
  return id.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ').toUpperCase();
}

/** A two-column split on a wide pane and one column on a narrow one. */
export function split(wide: boolean, left: string, right: string): string {
  if (!wide) return `<div class="col">${left}${right}</div>`;
  return `<div class="split"><div class="col">${left}</div><div class="col">${right}</div></div>`;
}

/** A column of cards. */
export function col(...parts: string[]): string {
  return `<div class="col">${parts.join('')}</div>`;
}

/**
 * True the first time `wire` sees an element, so a listener is added once.
 * The shell calls `wire` after every rewrite of the pane, and an element
 * the rewrite kept must not take a second listener.
 */
export function wireOnce(el: HTMLElement): boolean {
  if (el.dataset.wired === '1') return false;
  el.dataset.wired = '1';
  return true;
}
